import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FGM } from "../brand";
import ProductsTab from "./ProductsTab";
import UtmTab from "./UtmTab";
import ReviewsTab from "./ReviewsTab";
import AbandonedCartsTab from "./AbandonedCartsTab";
import UgcTab from "./UgcTab";
import PayoutsTab from "./PayoutsTab";
import BroadcastTab from "./BroadcastTab";
import OrdersTab from "./OrdersTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASS_KEY = "fgm_admin_password";

function Tabs({ value, onChange }) {
    const tabs = [
        { id: "stats", label: "Overview" },
        { id: "products", label: "Products" },
        { id: "reviews", label: "Reviews" },
        { id: "ugc", label: "UGC" },
        { id: "utm", label: "Campaigns" },
        { id: "abandoned", label: "Abandoned Carts" },
        { id: "countdown", label: "Next Drop" },
        { id: "launch", label: "Launch Drop" },
        { id: "referrals", label: "Referrals" },
        { id: "ambassadors", label: "Ambassadors" },
        { id: "orders", label: "Orders" },
        { id: "broadcast", label: "Broadcast" },
        { id: "payouts", label: "Payouts" },
        { id: "newsletter", label: "Newsletter" },
        { id: "drop", label: "Drop Notify" },
        { id: "inquiries", label: "Custom Inquiries" },
    ];
    return (
        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-8">
            {tabs.map((t) => (
                <button
                    key={t.id}
                    data-testid={`admin-tab-${t.id}`}
                    onClick={() => onChange(t.id)}
                    className={`px-4 py-3 font-display tracking-[0.2em] text-sm uppercase transition-colors ${
                        value === t.id
                            ? "text-white border-b-2 border-[#E63946] -mb-px"
                            : "text-zinc-500 hover:text-white"
                    }`}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <div className="bg-fgm-surface border border-white/10 p-6">
            <div className="text-xs font-display tracking-[0.3em] text-zinc-500 uppercase mb-2">
                {label}
            </div>
            <div
                className="font-display text-4xl md:text-5xl text-white"
                style={accent ? { color: accent } : {}}
            >
                {value}
            </div>
        </div>
    );
}

function Table({ columns, rows, empty }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="bg-fgm-surface border border-white/10 p-12 text-center text-zinc-500 font-display tracking-widest uppercase">
                {empty || "No data yet"}
            </div>
        );
    }
    return (
        <div className="bg-fgm-surface border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-black/40 border-b border-white/10">
                    <tr>
                        {columns.map((c) => (
                            <th
                                key={c.key}
                                className="text-left px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase whitespace-nowrap"
                            >
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr
                            key={r.id || i}
                            className="border-b border-white/5 hover:bg-white/[0.02]"
                        >
                            {columns.map((c) => (
                                <td
                                    key={c.key}
                                    className="px-4 py-3 text-zinc-300 align-top max-w-xs"
                                >
                                    {c.render ? c.render(r) : r[c.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

function exportCSV(rows, filename) {
    if (!rows || !rows.length) {
        toast.info("Nothing to export yet.");
        return;
    }
    const keys = Object.keys(rows[0]);
    const escape = (v) => {
        if (v === null || v === undefined) return "";
        const s = typeof v === "object" ? JSON.stringify(v) : String(v);
        return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [
        keys.join(","),
        ...rows.map((r) => keys.map((k) => escape(r[k])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function Admin() {
    const [password, setPassword] = useState(() => sessionStorage.getItem(PASS_KEY) || "");
    const [authed, setAuthed] = useState(false);
    const [pwInput, setPwInput] = useState("");
    const [loading, setLoading] = useState(false);

    const [tab, setTab] = useState("stats");
    const [stats, setStats] = useState(null);
    const [ambassadors, setAmbassadors] = useState([]);
    const [newsletter, setNewsletter] = useState([]);
    const [dropNotify, setDropNotify] = useState([]);
    const [orders, setOrders] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [launchLog, setLaunchLog] = useState([]);
    const [launching, setLaunching] = useState(null);
    const [referrals, setReferrals] = useState([]);

    // Ambassadors tab — status filter + multi-select
    const [ambStatusFilter, setAmbStatusFilter] = useState("pending");
    const [selectedAmbIds, setSelectedAmbIds] = useState(new Set());
    const [bulkAccepting, setBulkAccepting] = useState(false);

    // Countdown state
    const [drop, setDrop] = useState(null);
    const [dropForm, setDropForm] = useState({
        label: "",
        tagline: "",
        target_at: "",
        product_id: "",
        active: true,
    });
    const [savingDrop, setSavingDrop] = useState(false);

    const buildHeaders = (pw) => ({ headers: { "X-Admin-Password": pw || password } });

    const loadAll = async (pw) => {
        const h = buildHeaders(pw);
        try {
            const [s, a, n, d, o, c, lg, dr, rf, inq] = await Promise.all([
                axios.get(`${API}/admin/stats`, h),
                axios.get(`${API}/admin/ambassadors`, h),
                axios.get(`${API}/admin/newsletter`, h),
                axios.get(`${API}/admin/drop-notifies`, h),
                axios.get(`${API}/admin/orders`, h),
                axios.get(`${API}/admin/launch-candidates`, h),
                axios.get(`${API}/admin/launch-log`, h),
                axios.get(`${API}/admin/drop`, h),
                axios.get(`${API}/admin/referrals`, h),
                axios.get(`${API}/admin/custom-inquiries`, h),
            ]);
            setStats(s.data);
            setAmbassadors(a.data);
            setNewsletter(n.data);
            setDropNotify(d.data);
            setOrders(o.data);
            setCandidates(c.data);
            setLaunchLog(lg.data);
            setReferrals(rf.data);
            setInquiries(inq.data);
            const cur = dr.data?.drop;
            setDrop(cur);
            if (cur) {
                // Format ISO → datetime-local (yyyy-MM-ddTHH:mm) in local tz
                const dt = new Date(cur.target_at);
                const tzOff = dt.getTimezoneOffset() * 60000;
                const localIso = new Date(dt.getTime() - tzOff).toISOString().slice(0, 16);
                setDropForm({
                    label: cur.label || "",
                    tagline: cur.tagline || "",
                    target_at: localIso,
                    product_id: cur.product_id || "",
                    active: !!cur.active,
                });
            }
            setAuthed(true);
        } catch (err) {
            if (err?.response?.status === 401) {
                setAuthed(false);
                sessionStorage.removeItem(PASS_KEY);
            } else {
                toast.error("Failed to load data");
            }
        }
    };

    const saveDrop = async (e) => {
        e.preventDefault();
        if (!dropForm.label || !dropForm.target_at) {
            toast.error("Label and target date are required");
            return;
        }
        setSavingDrop(true);
        try {
            const h = buildHeaders(password);
            // Convert datetime-local back to ISO with TZ
            const targetIso = new Date(dropForm.target_at).toISOString();
            await axios.put(
                `${API}/admin/drop`,
                {
                    label: dropForm.label,
                    tagline: dropForm.tagline || null,
                    target_at: targetIso,
                    product_id: dropForm.product_id || null,
                    active: dropForm.active,
                },
                h,
            );
            toast.success("Countdown saved. Public site updated.");
            await loadAll(password);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Save failed";
            toast.error(typeof msg === "string" ? msg : "Save failed");
        } finally {
            setSavingDrop(false);
        }
    };

    const clearDrop = async () => {
        if (!window.confirm("Remove the countdown from the public site?")) return;
        try {
            const h = buildHeaders(password);
            await axios.delete(`${API}/admin/drop`, h);
            toast.success("Countdown cleared");
            setDrop(null);
            setDropForm({ label: "", tagline: "", target_at: "", product_id: "", active: true });
        } catch (err) {
            toast.error("Failed to clear");
        }
    };

    const launchDrop = async (product) => {        const confirmed = window.confirm(
            `Launch "${product.name}"?\n\nIt will flip from Coming Soon to LIVE on the public shop, and ${product.notify_count} notify-list subscribers will be queued for launch emails.`,
        );
        if (!confirmed) return;
        setLaunching(product.product_id);
        try {
            const h = buildHeaders(password);
            const res = await axios.post(`${API}/admin/launch-drop`, { product_id: product.product_id }, h);
            toast.success(res.data.message || `${product.name} launched.`);
            await loadAll(password);
        } catch (err) {
            toast.error("Launch failed");
        } finally {
            setLaunching(null);
        }
    };

    useEffect(() => {
        if (password) loadAll(password);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await axios.post(`${API}/admin/login`, { password: pwInput });
            if (res.data?.ok) {
                setPassword(pwInput);
                sessionStorage.setItem(PASS_KEY, pwInput);
                setAuthed(true);
                await loadAll(pwInput);
                setPwInput("");
                toast.success("Welcome back, operator.");
            }
        } catch (err) {
            toast.error("Wrong password");
        } finally {
            setLoading(false);
        }
    };

    if (!authed) {
        return (
            <div className="min-h-screen bg-fgm-dark grain flex items-center justify-center px-6">
                <form
                    onSubmit={login}
                    data-testid="admin-login-form"
                    className="bg-fgm-surface border border-white/10 p-8 md:p-10 max-w-md w-full"
                >
                    <img src={FGM.logos.white} alt="FGM" className="h-14 w-14 mb-5"  loading="lazy" decoding="async" />
                    <div className="fgm-stripe-divider w-16 mb-5" />
                    <h1 className="font-display text-4xl text-white uppercase mb-2">
                        Operator Access
                    </h1>
                    <p className="text-zinc-400 text-sm mb-8">
                        FGM Apparel internal dashboard.
                    </p>
                    <label className="fgm-label">Password</label>
                    <input
                        data-testid="admin-password-input"
                        type="password"
                        required
                        value={pwInput}
                        onChange={(e) => setPwInput(e.target.value)}
                        className="fgm-input mb-5"
                        placeholder="Enter admin password"
                    />
                    <button
                        data-testid="admin-login-submit"
                        disabled={loading}
                        className="btn-primary w-full disabled:opacity-50"
                    >
                        {loading ? "Checking…" : "Enter"}
                    </button>
                    <Link
                        to="/"
                        className="block mt-6 text-center text-xs font-display tracking-[0.3em] text-zinc-500 hover:text-white uppercase"
                    >
                        ← Back to site
                    </Link>
                </form>
            </div>
        );
    }

    return (
        <div data-testid="admin-page" className="min-h-screen bg-fgm-dark grain pb-24">
            <header className="bg-black/80 backdrop-blur-xl border-b border-white/10 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={FGM.logos.white} alt="FGM" className="h-9 w-9"  loading="lazy" decoding="async" />
                        <div>
                            <div className="font-display text-lg tracking-[0.2em] text-white">FGM ADMIN</div>
                            <div className="text-[10px] tracking-[0.3em] text-zinc-500 font-display uppercase">
                                Operator Console
                            </div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <button
                            data-testid="admin-refresh"
                            onClick={() => loadAll(password)}
                            className="text-xs font-display tracking-[0.3em] uppercase text-zinc-400 hover:text-white"
                        >
                            ↻ Refresh
                        </button>
                        <button
                            data-testid="admin-wipe-tests"
                            onClick={async () => {
                                if (!window.confirm(
                                    "Wipe ALL test/QA data across every collection (ambassadors, newsletter, drop notify, custom inquiries, referrals, orders with @example.com / @test.com / TEST_ entries)?\n\nThis cannot be undone."
                                )) return;
                                try {
                                    const res = await axios.post(
                                        `${API}/admin/cleanup-all-tests`,
                                        null,
                                        { headers: { "X-Admin-Password": password } }
                                    );
                                    const { total_deleted, by_collection } = res.data;
                                    const detail = Object.entries(by_collection)
                                        .filter(([, v]) => v > 0)
                                        .map(([k, v]) => `${v} ${k.replace("_", " ")}`)
                                        .join(" · ");
                                    toast.success(
                                        total_deleted === 0
                                            ? "Already clean — no test rows found"
                                            : `Wiped ${total_deleted} test rows · ${detail}`,
                                        { duration: 5000 }
                                    );
                                    loadAll();
                                } catch {
                                    toast.error("Wipe failed — check console");
                                }
                            }}
                            className="text-xs font-display tracking-[0.3em] uppercase text-zinc-400 hover:text-[#E63946]"
                        >
                            ⌫ Wipe Test Data
                        </button>
                        <button
                            data-testid="admin-logout"
                            onClick={() => {
                                sessionStorage.removeItem(PASS_KEY);
                                setPassword("");
                                setAuthed(false);
                            }}
                            className="text-xs font-display tracking-[0.3em] uppercase text-zinc-400 hover:text-[#E63946]"
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
                <Tabs value={tab} onChange={setTab} />

                {tab === "stats" && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <StatCard label="Ambassadors" value={stats?.ambassadors ?? 0} accent="#E63946" />
                        <StatCard label="Newsletter" value={stats?.newsletter ?? 0} />
                        <StatCard label="Drop Notify" value={stats?.drop_notifies ?? 0} accent="#1D4ED8" />
                        <StatCard label="Paid Orders" value={`${stats?.orders_paid ?? 0}/${stats?.orders_total ?? 0}`} />
                    </div>
                )}
                {tab === "stats" && (
                    <FunnelWidget password={password} />
                )}
                {tab === "stats" && stats?.notify_email && (
                    <div className="bg-fgm-surface border border-white/10 p-6">
                        <div className="text-xs font-display tracking-[0.3em] text-zinc-500 uppercase mb-2">
                            Notification Email
                        </div>
                        <div className="text-white font-body">{stats.notify_email}</div>
                        <p className="text-xs text-zinc-500 mt-2">
                            (Resend email integration is not wired yet — add a Resend API key to enable automatic emails on new applications.)
                        </p>
                    </div>
                )}

                {tab === "products" && (
                    <ProductsTab password={password} />
                )}

                {tab === "reviews" && (
                    <ReviewsTab password={password} />
                )}

                {tab === "ugc" && (
                    <UgcTab password={password} />
                )}

                {tab === "payouts" && (
                    <PayoutsTab password={password} />
                )}

                {tab === "broadcast" && (
                    <BroadcastTab password={password} />
                )}

                {tab === "orders" && (
                    <OrdersTab password={password} />
                )}

                {tab === "utm" && (
                    <UtmTab password={password} />
                )}

                {tab === "abandoned" && (
                    <AbandonedCartsTab password={password} />
                )}

                {tab === "countdown" && (
                    <div data-testid="countdown-tab" className="space-y-6">
                        <div className="bg-fgm-surface border border-white/10 p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="fgm-stripe-divider w-12" />
                                <span className="font-display tracking-[0.3em] text-xs text-zinc-400 uppercase">
                                    Next Drop Countdown
                                </span>
                            </div>
                            <h3 className="font-display text-3xl text-white uppercase mb-1">
                                Public Countdown Timer
                            </h3>
                            <p className="text-sm text-zinc-400">
                                Set a future date and label. A live countdown banner appears
                                on the homepage right under the hero. Clears automatically
                                6 hours after the target time. Leave blank to hide it.
                            </p>
                        </div>

                        <form
                            onSubmit={saveDrop}
                            data-testid="countdown-form"
                            className="bg-fgm-surface border border-white/10 p-6 space-y-5"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="fgm-label">Label</label>
                                    <input
                                        data-testid="countdown-label-input"
                                        type="text"
                                        required
                                        value={dropForm.label}
                                        onChange={(e) =>
                                            setDropForm({ ...dropForm, label: e.target.value })
                                        }
                                        className="fgm-input"
                                        placeholder="Drop 02"
                                    />
                                </div>
                                <div>
                                    <label className="fgm-label">Target Date / Time</label>
                                    <input
                                        data-testid="countdown-target-input"
                                        type="datetime-local"
                                        required
                                        value={dropForm.target_at}
                                        onChange={(e) =>
                                            setDropForm({ ...dropForm, target_at: e.target.value })
                                        }
                                        className="fgm-input"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="fgm-label">Tagline (optional)</label>
                                    <input
                                        data-testid="countdown-tagline-input"
                                        type="text"
                                        value={dropForm.tagline}
                                        onChange={(e) =>
                                            setDropForm({ ...dropForm, tagline: e.target.value })
                                        }
                                        className="fgm-input"
                                        placeholder="Limited Capsule · 250 Pieces"
                                    />
                                </div>
                                <div>
                                    <label className="fgm-label">Featured Product (optional)</label>
                                    <select
                                        data-testid="countdown-product-input"
                                        value={dropForm.product_id}
                                        onChange={(e) =>
                                            setDropForm({ ...dropForm, product_id: e.target.value })
                                        }
                                        className="fgm-input"
                                    >
                                        <option value="">— None —</option>
                                        {candidates.map((c) => (
                                            <option key={c.product_id} value={c.product_id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <label className="inline-flex items-center gap-3 cursor-pointer">
                                        <input
                                            data-testid="countdown-active-input"
                                            type="checkbox"
                                            checked={dropForm.active}
                                            onChange={(e) =>
                                                setDropForm({ ...dropForm, active: e.target.checked })
                                            }
                                            className="accent-[#E63946] w-5 h-5"
                                        />
                                        <span className="fgm-label !mb-0">Active (visible to public)</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                <button
                                    data-testid="countdown-save"
                                    type="submit"
                                    disabled={savingDrop}
                                    className="btn-primary disabled:opacity-50"
                                >
                                    {savingDrop ? "Saving…" : drop ? "Update Countdown" : "Save Countdown"}
                                </button>
                                {drop && (
                                    <button
                                        data-testid="countdown-clear"
                                        type="button"
                                        onClick={clearDrop}
                                        className="btn-outline"
                                    >
                                        Remove From Site
                                    </button>
                                )}
                            </div>
                        </form>

                        {drop && (
                            <div className="bg-fgm-surface border border-white/10 p-6">
                                <div className="text-xs font-display tracking-[0.3em] text-zinc-500 uppercase mb-3">
                                    Currently Live On Site
                                </div>
                                <div className="font-display text-2xl text-white uppercase">{drop.label}</div>
                                {drop.tagline && (
                                    <div className="text-zinc-400 mt-1">{drop.tagline}</div>
                                )}
                                <div className="text-sm text-zinc-500 mt-3 font-mono">
                                    Target: {new Date(drop.target_at).toLocaleString()}
                                </div>
                                <div
                                    className={`text-xs font-display tracking-[0.3em] uppercase mt-2 ${
                                        drop.active ? "text-[#E63946]" : "text-zinc-500"
                                    }`}
                                >
                                    {drop.active ? "● Active" : "○ Hidden"}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "launch" && (
                    <div data-testid="launch-tab" className="space-y-8">
                        <div className="bg-fgm-surface border border-white/10 p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="fgm-stripe-divider w-12" />
                                <span className="font-display tracking-[0.3em] text-xs text-zinc-400 uppercase">
                                    Drop Launcher
                                </span>
                            </div>
                            <h3 className="font-display text-3xl text-white uppercase mb-1">
                                Coming-Soon Products
                            </h3>
                            <p className="text-sm text-zinc-400">
                                Flip a product from "Coming Soon" to LIVE on the public shop. The
                                notify-list for that drop gets queued for launch emails
                                (add a Resend API key in <span className="text-white">backend/.env</span> as
                                <span className="text-white"> RESEND_API_KEY</span> to enable auto-sending).
                            </p>
                        </div>

                        {candidates.length === 0 ? (
                            <div className="bg-fgm-surface border border-white/10 p-12 text-center text-zinc-500 font-display tracking-widest uppercase">
                                No coming-soon products to launch
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {candidates.map((c) => (
                                    <div
                                        key={c.product_id}
                                        data-testid={`launch-card-${c.product_id}`}
                                        className="bg-fgm-surface border border-white/10 flex"
                                    >
                                        <img
                                            src={c.image}
                                            alt={c.name}
                                            className="w-32 h-32 object-cover bg-black flex-shrink-0"
                                            style={{ filter: "grayscale(60%) brightness(0.85)" }}
                                         loading="lazy" decoding="async" />
                                        <div className="p-4 flex-1 flex flex-col">
                                            <div className="text-[10px] font-display tracking-[0.3em] text-[#1D4ED8] uppercase mb-1">
                                                Coming Soon · {c.category}
                                            </div>
                                            <div className="font-display text-xl text-white uppercase leading-tight mb-1">
                                                {c.name}
                                            </div>
                                            <div className="text-sm text-zinc-400 mb-3">
                                                ${Number.isInteger(c.price) ? c.price : c.price.toFixed(2)} ·{" "}
                                                <span className="text-white">
                                                    {c.notify_count}
                                                </span>{" "}
                                                on notify list
                                            </div>
                                            <button
                                                data-testid={`launch-btn-${c.product_id}`}
                                                onClick={() => launchDrop(c)}
                                                disabled={launching === c.product_id}
                                                className="btn-primary !py-2 !text-sm self-start disabled:opacity-50"
                                            >
                                                {launching === c.product_id
                                                    ? "Launching…"
                                                    : "Launch Drop →"}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {launchLog.length > 0 && (
                            <div>
                                <h4 className="font-display text-xl text-white uppercase mb-3 mt-8">
                                    Launch History
                                </h4>
                                <Table
                                    columns={[
                                        { key: "product_name", label: "Product" },
                                        {
                                            key: "notify_count",
                                            label: "Subscribers",
                                            render: (r) => (
                                                <span className="font-display text-white">
                                                    {r.notify_count}
                                                </span>
                                            ),
                                        },
                                        {
                                            key: "email_status",
                                            label: "Email Status",
                                            render: (r) => (
                                                <span className="font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">
                                                    {r.email_status === "sent"
                                                        ? "✓ Sent"
                                                        : "Queued — add Resend key"}
                                                </span>
                                            ),
                                        },
                                        {
                                            key: "launched_at",
                                            label: "Launched",
                                            render: (r) => fmtDate(r.launched_at),
                                        },
                                    ]}
                                    rows={launchLog}
                                />
                            </div>
                        )}
                    </div>
                )}

                {tab === "referrals" && (
                    <div data-testid="referrals-tab" className="space-y-6">
                        <div className="bg-fgm-surface border border-white/10 p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="fgm-stripe-divider w-12" />
                                <span className="font-display tracking-[0.3em] text-xs text-zinc-400 uppercase">
                                    Growth Loop
                                </span>
                            </div>
                            <h3 className="font-display text-3xl text-white uppercase mb-1">
                                Referral Leaderboard
                            </h3>
                            <p className="text-sm text-zinc-400">
                                Every ambassador gets a unique share link (e.g.{" "}
                                <span className="text-white">/?ref=name-xxxx</span>). Newsletter
                                signups, ambassador applications, and orders that come through
                                their link are credited below — sorted by paid orders, then
                                revenue, then clicks.
                            </p>
                        </div>

                        <div className="flex justify-end">
                            <button
                                data-testid="export-referrals"
                                onClick={() => exportCSV(referrals, "fgm-referrals.csv")}
                                className="text-xs font-display tracking-[0.3em] uppercase text-zinc-400 hover:text-white border border-white/10 px-3 py-2"
                            >
                                ↓ Export CSV
                            </button>
                        </div>

                        <Table
                            columns={[
                                {
                                    key: "owner_name",
                                    label: "Ambassador",
                                    render: (r) => (
                                        <div>
                                            <div className="text-white">{r.owner_name || "—"}</div>
                                            <div className="text-xs text-zinc-500">{r.owner_email || ""}</div>
                                        </div>
                                    ),
                                },
                                {
                                    key: "code",
                                    label: "Code",
                                    render: (r) => (
                                        <button
                                            type="button"
                                            data-testid={`copy-ref-${r.code}`}
                                            onClick={async () => {
                                                const url = `${window.location.origin}/?ref=${r.code}`;
                                                try {
                                                    await navigator.clipboard.writeText(url);
                                                    toast.success("Share link copied");
                                                } catch {
                                                    toast.error("Copy failed");
                                                }
                                            }}
                                            className="font-mono text-xs text-white border border-white/10 px-2 py-1 hover:bg-white/5"
                                        >
                                            {r.code} ⧉
                                        </button>
                                    ),
                                },
                                {
                                    key: "clicks",
                                    label: "Clicks",
                                    render: (r) => <span className="font-display text-white">{r.clicks ?? 0}</span>,
                                },
                                {
                                    key: "newsletter_signups",
                                    label: "Newsletter",
                                    render: (r) => <span className="font-display text-white">{r.newsletter_signups ?? 0}</span>,
                                },
                                {
                                    key: "ambassador_signups",
                                    label: "Apps",
                                    render: (r) => <span className="font-display text-white">{r.ambassador_signups ?? 0}</span>,
                                },
                                {
                                    key: "paid_orders",
                                    label: "Orders",
                                    render: (r) => <span className="font-display text-[#E63946]">{r.paid_orders ?? 0}</span>,
                                },
                                {
                                    key: "revenue",
                                    label: "Revenue",
                                    render: (r) => (
                                        <span className="font-display text-white">
                                            ${Number(r.revenue || 0).toFixed(2)}
                                        </span>
                                    ),
                                },
                                {
                                    key: "created_at",
                                    label: "Since",
                                    render: (r) => fmtDate(r.created_at),
                                },
                            ]}
                            rows={referrals}
                            empty="No referrals yet — apply as ambassador to generate one"
                        />
                    </div>
                )}

                {tab === "ambassadors" && (() => {
                    const pendingRows = ambassadors.filter((a) => a.status !== "accepted");
                    const acceptedRows = ambassadors.filter((a) => a.status === "accepted");
                    const filteredAmbs =
                        ambStatusFilter === "pending"
                            ? pendingRows
                            : ambStatusFilter === "accepted"
                            ? acceptedRows
                            : ambassadors;
                    const showCheckboxes = ambStatusFilter === "pending";
                    const visibleSelectable = filteredAmbs.filter((a) => a.status !== "accepted");
                    const allVisibleSelected =
                        visibleSelectable.length > 0 &&
                        visibleSelectable.every((a) => selectedAmbIds.has(a.id));
                    const toggleOne = (id) => {
                        setSelectedAmbIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(id)) next.delete(id);
                            else next.add(id);
                            return next;
                        });
                    };
                    const toggleAllVisible = () => {
                        if (allVisibleSelected) {
                            setSelectedAmbIds(new Set());
                        } else {
                            setSelectedAmbIds(new Set(visibleSelectable.map((a) => a.id)));
                        }
                    };
                    const clearSelection = () => setSelectedAmbIds(new Set());
                    const runBulkAccept = async () => {
                        const ids = Array.from(selectedAmbIds);
                        if (ids.length === 0) return;
                        if (!window.confirm(
                            `Bulk-accept ${ids.length} ambassador application${ids.length === 1 ? "" : "s"}?\n\n` +
                            `Each gets a personal Stripe discount code + branded welcome email.\n` +
                            `This action cannot be undone.`
                        )) return;
                        setBulkAccepting(true);
                        try {
                            const res = await axios.post(
                                `${API}/admin/ambassadors/bulk-accept`,
                                { ids },
                                { headers: { "X-Admin-Password": password } },
                            );
                            const { accepted, skipped, failed } = res.data;
                            toast.success(
                                `Accepted ${accepted.length} · Skipped ${skipped.length} · Failed ${failed.length}`,
                                { duration: 6000 }
                            );
                            if (failed.length > 0) {
                                console.warn("[bulk-accept] failures:", failed);
                            }
                            clearSelection();
                            await loadAll();
                        } catch (err) {
                            toast.error(err?.response?.data?.detail || "Bulk accept failed");
                        } finally {
                            setBulkAccepting(false);
                        }
                    };

                    return (
                    <>
                        {/* Status filter pills */}
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            {[
                                { key: "pending", label: "Pending", count: pendingRows.length },
                                { key: "accepted", label: "Accepted", count: acceptedRows.length },
                                { key: "all", label: "All", count: ambassadors.length },
                            ].map((f) => {
                                const active = ambStatusFilter === f.key;
                                return (
                                    <button
                                        key={f.key}
                                        data-testid={`amb-filter-${f.key}`}
                                        onClick={() => {
                                            setAmbStatusFilter(f.key);
                                            clearSelection();
                                        }}
                                        className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border transition-colors ${
                                            active
                                                ? "bg-[#E63946] border-[#E63946] text-white"
                                                : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                                        }`}
                                    >
                                        {f.label} <span className="opacity-70">({f.count})</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Bulk-select action bar (only visible when items selected) */}
                        {showCheckboxes && selectedAmbIds.size > 0 && (
                            <div
                                data-testid="bulk-accept-bar"
                                className="flex flex-wrap items-center justify-between gap-3 mb-3 px-4 py-3 border border-[#E63946]/60 bg-[#E63946]/10"
                            >
                                <div className="font-display text-[11px] tracking-[0.25em] uppercase text-white">
                                    <span data-testid="bulk-selected-count" className="text-[#E63946] font-bold">
                                        {selectedAmbIds.size}
                                    </span>{" "}
                                    selected
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        data-testid="bulk-accept-confirm"
                                        disabled={bulkAccepting}
                                        onClick={runBulkAccept}
                                        className="text-[10px] font-display tracking-[0.25em] uppercase px-4 py-1.5 bg-[#E63946] text-white hover:bg-white hover:text-[#E63946] transition-colors disabled:opacity-50"
                                    >
                                        {bulkAccepting ? "Accepting…" : `✓ Bulk Accept (${selectedAmbIds.size})`}
                                    </button>
                                    <button
                                        data-testid="bulk-accept-clear"
                                        onClick={clearSelection}
                                        className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-white/20 text-zinc-300 hover:text-white"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 border border-white/10 bg-black/40">
                            <span className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 mr-1">
                                🚀 Summer Heat Drop Launch:
                            </span>
                            {[
                                { slug: "ambassador-early", label: "Ambassador Early Access", subject: "48-hour pre-launch invite (ambassadors only · EARLYBIRD code)" },
                                { slug: "t-minus-7", label: "T-7 Days", subject: "Drop in 7 days (ambassadors + newsletter)" },
                                { slug: "t-minus-24", label: "T-24 Hours", subject: "Drop tomorrow (ambassadors + newsletter)" },
                                { slug: "t-minus-1", label: "T-1 Hour", subject: "Drop in 60 min (ambassadors + newsletter)" },
                                { slug: "live", label: "LIVE NOW", subject: "Drop is live (ambassadors + newsletter)" },
                            ].map((t) => (
                                <button
                                    key={t.slug}
                                    data-testid={`launch-broadcast-${t.slug}`}
                                    onClick={async () => {
                                        if (!window.confirm(`Send "${t.label}" launch email?\n\n${t.subject}\n\nThis is irreversible — emails fire immediately.`)) return;
                                        try {
                                            const res = await axios.post(
                                                `${API}/admin/launch-broadcast/${t.slug}`,
                                                null,
                                                { headers: { "X-Admin-Password": password } },
                                            );
                                            const { sent, failed, total } = res.data;
                                            toast.success(`${t.label} sent to ${sent}/${total} · ${failed} failed`, { duration: 6000 });
                                        } catch (err) {
                                            toast.error(err?.response?.data?.detail || "Launch broadcast failed");
                                        }
                                    }}
                                    className="text-[10px] font-display tracking-[0.2em] uppercase px-2.5 py-1.5 border border-white/15 text-white hover:bg-[#E63946] hover:border-[#E63946] transition-colors"
                                    title={t.subject}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2 mb-3">
                            <button
                                data-testid="broadcast-share-feature"
                                onClick={async () => {
                                    if (!window.confirm(
                                        "Send the 'Share-Any-Box is live' announcement to EVERY accepted ambassador?\n\n" +
                                        "Each ambassador will get a personalized email with their own referral link + discount code. " +
                                        "Safe to skip if you've already sent it — re-running will send duplicates."
                                    )) return;
                                    try {
                                        const res = await axios.post(
                                            `${API}/admin/ambassadors/broadcast`,
                                            null,
                                            { headers: { "X-Admin-Password": password } },
                                        );
                                        const { sent, failed, total } = res.data;
                                        toast.success(
                                            `Broadcast sent to ${sent}/${total} · ${failed} failed`,
                                            { duration: 6000 }
                                        );
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Broadcast failed");
                                    }
                                }}
                                className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white transition-colors"
                            >
                                📣 Notify of Share Feature
                            </button>
                            <button
                                data-testid="cleanup-test-ambassadors"
                                onClick={async () => {
                                    if (!window.confirm("Delete all test/QA ambassador rows (TEST prefix or @example.com emails)?")) return;
                                    try {
                                        const res = await axios.post(
                                            `${API}/admin/ambassadors/cleanup-tests`,
                                            null,
                                            { headers: { "X-Admin-Password": password } }
                                        );
                                        toast.success(`Removed ${res.data.deleted} test rows`);
                                        loadAll();
                                    } catch {
                                        toast.error("Cleanup failed");
                                    }
                                }}
                                className="text-xs font-display tracking-[0.3em] uppercase text-zinc-400 hover:text-[#E63946] border border-white/10 px-3 py-2"
                            >
                                ⌫ Clean Test Rows
                            </button>
                            <button
                                data-testid="export-ambassadors"
                                onClick={() => exportCSV(filteredAmbs, `fgm-ambassadors-${ambStatusFilter}.csv`)}
                                className="text-xs font-display tracking-[0.3em] uppercase text-zinc-400 hover:text-white border border-white/10 px-3 py-2"
                            >
                                ↓ Export CSV
                            </button>
                        </div>
                        <Table
                            columns={[
                                ...(showCheckboxes
                                    ? [{
                                        key: "__select",
                                        label: (
                                            <input
                                                type="checkbox"
                                                data-testid="bulk-select-all"
                                                checked={allVisibleSelected}
                                                onChange={toggleAllVisible}
                                                disabled={visibleSelectable.length === 0}
                                                className="accent-[#E63946] cursor-pointer h-4 w-4"
                                            />
                                        ),
                                        render: (r) =>
                                            r.status === "accepted" ? (
                                                <span className="text-zinc-700">—</span>
                                            ) : (
                                                <input
                                                    type="checkbox"
                                                    data-testid={`bulk-select-${r.id}`}
                                                    checked={selectedAmbIds.has(r.id)}
                                                    onChange={() => toggleOne(r.id)}
                                                    className="accent-[#E63946] cursor-pointer h-4 w-4"
                                                />
                                            ),
                                    }]
                                    : []),
                                { key: "name", label: "Name" },
                                { key: "email", label: "Email" },
                                { key: "location", label: "Location" },
                                { key: "social_handle", label: "Social" },
                                { key: "sport", label: "Sport" },
                                {
                                    key: "why_fgm",
                                    label: "Why FGM",
                                    render: (r) => (
                                        <div className="max-w-md text-zinc-400 whitespace-normal">
                                            {r.why_fgm}
                                        </div>
                                    ),
                                },
                                {
                                    key: "status",
                                    label: "Status",
                                    render: (r) => (
                                        <span
                                            className={`font-display text-[10px] tracking-[0.25em] uppercase ${
                                                r.status === "accepted"
                                                    ? "text-[#E63946]"
                                                    : "text-zinc-500"
                                            }`}
                                        >
                                            {r.status === "accepted" ? "● Accepted" : "○ Pending"}
                                        </span>
                                    ),
                                },
                                {
                                    key: "discount_code",
                                    label: "Code",
                                    render: (r) =>
                                        r.discount_code ? (
                                            <button
                                                data-testid={`copy-discount-${r.id}`}
                                                onClick={() => {
                                                    navigator.clipboard.writeText(r.discount_code);
                                                    toast.success(`Copied ${r.discount_code}`);
                                                }}
                                                className="font-mono text-[11px] text-[#E63946] hover:text-white border border-[#E63946]/30 hover:border-[#E63946] px-2 py-1 transition-colors"
                                                title="Click to copy"
                                            >
                                                {r.discount_code}
                                            </button>
                                        ) : (
                                            <span className="text-zinc-600 text-xs">—</span>
                                        ),
                                },
                                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                                {
                                    key: "actions",
                                    label: "",
                                    render: (r) => (
                                        <div className="flex items-center gap-2 justify-end whitespace-nowrap">
                                            <button
                                                data-testid={`accept-ambassador-${r.id}`}
                                                onClick={async () => {
                                                    const label = r.status === "accepted" ? "Resend" : "Accept";
                                                    if (!window.confirm(
                                                        `${label} ${r.name} as an FGM ambassador? They'll receive a welcome email with their referral code and link.`
                                                    )) return;
                                                    try {
                                                        const res = await axios.post(
                                                            `${API}/admin/ambassadors/${r.id}/accept`,
                                                            null,
                                                            { headers: { "X-Admin-Password": password } }
                                                        );
                                                        toast.success(`Welcome email sent to ${res.data.emailed}`);
                                                        loadAll();
                                                    } catch (err) {
                                                        toast.error(err?.response?.data?.detail || "Accept failed");
                                                    }
                                                }}
                                                className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border transition-colors ${
                                                    r.status === "accepted"
                                                        ? "border-white/10 text-zinc-500 hover:text-white hover:border-white/30"
                                                        : "border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white"
                                                }`}
                                            >
                                                {r.status === "accepted" ? "↻ Resend" : "✓ Accept"}
                                            </button>
                                            <button
                                                data-testid={`delete-ambassador-${r.id}`}
                                                onClick={async () => {
                                                    if (!window.confirm(`Delete ${r.name}'s application?`)) return;
                                                    try {
                                                        await axios.delete(
                                                            `${API}/admin/ambassadors/${r.id}`,
                                                            { headers: { "X-Admin-Password": password } }
                                                        );
                                                        toast.success("Deleted");
                                                        loadAll();
                                                    } catch {
                                                        toast.error("Delete failed");
                                                    }
                                                }}
                                                className="text-zinc-500 hover:text-[#E63946] text-lg leading-none px-2"
                                                aria-label="Delete"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ),
                                },
                            ]}
                            rows={filteredAmbs}
                            empty={
                                ambStatusFilter === "pending"
                                    ? "No pending applications · all caught up"
                                    : ambStatusFilter === "accepted"
                                    ? "No accepted ambassadors yet"
                                    : "No ambassador applications yet"
                            }
                        />
                    </>
                    );
                })()}

                {tab === "newsletter" && (
                    <>
                        <div className="flex justify-end mb-3">
                            <button
                                data-testid="export-newsletter"
                                onClick={() => exportCSV(newsletter, "fgm-newsletter.csv")}
                                className="text-xs font-display tracking-[0.3em] uppercase text-zinc-400 hover:text-white border border-white/10 px-3 py-2"
                            >
                                ↓ Export CSV
                            </button>
                        </div>
                        <Table
                            columns={[
                                { key: "email", label: "Email" },
                                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                            ]}
                            rows={newsletter}
                            empty="No newsletter signups yet"
                        />
                    </>
                )}

                {tab === "drop" && (
                    <Table
                        columns={[
                            { key: "email", label: "Email" },
                            { key: "product_id", label: "Product" },
                            { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                        ]}
                        rows={dropNotify}
                        empty="No drop notifications yet"
                    />
                )}

                {tab === "inquiries" && (
                    <Table
                        columns={[
                            { key: "name", label: "Name" },
                            { key: "email", label: "Email" },
                            { key: "project_type", label: "Project" },
                            { key: "quantity", label: "Qty" },
                            { key: "deadline", label: "Deadline", render: (r) => r.deadline || "—" },
                            {
                                key: "message",
                                label: "Details",
                                render: (r) => (
                                    <span className="block max-w-md text-zinc-300 text-xs leading-relaxed line-clamp-3 whitespace-pre-wrap">
                                        {r.message}
                                    </span>
                                ),
                            },
                            {
                                key: "attachments",
                                label: "Files",
                                render: (r) => {
                                    const atts = r.attachments || [];
                                    if (atts.length === 0) {
                                        return <span className="text-zinc-600 text-xs">—</span>;
                                    }
                                    return (
                                        <div className="flex flex-col gap-1 max-w-[220px]">
                                            {atts.map((a) => (
                                                <a
                                                    key={a.id}
                                                    href={`${API}/admin/inquiries/${r.id}/files/${a.id}`}
                                                    data-testid={`download-attachment-${a.id}`}
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        try {
                                                            const res = await axios.get(
                                                                `${API}/admin/inquiries/${r.id}/files/${a.id}`,
                                                                {
                                                                    headers: { "X-Admin-Password": password },
                                                                    responseType: "blob",
                                                                },
                                                            );
                                                            const url = window.URL.createObjectURL(res.data);
                                                            const link = document.createElement("a");
                                                            link.href = url;
                                                            link.download = a.filename || "file";
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            link.remove();
                                                            setTimeout(() => window.URL.revokeObjectURL(url), 0);
                                                        } catch (err) {
                                                            toast.error("Download failed");
                                                        }
                                                    }}
                                                    className="text-[11px] text-[#E63946] hover:text-white truncate"
                                                    title={`${a.filename} (${Math.round((a.size || 0) / 1024)} KB)`}
                                                >
                                                    📎 {a.filename}
                                                </a>
                                            ))}
                                        </div>
                                    );
                                },
                            },
                            { key: "created_at", label: "Received", render: (r) => fmtDate(r.created_at) },
                        ]}
                        rows={inquiries}
                        empty="No custom-order inquiries yet"
                    />
                )}

                {tab === "orders" && (
                    <>
                        <div className="flex justify-end gap-2 mb-3 flex-wrap">
                            <button
                                data-testid="create-drop-discount"
                                onClick={async () => {
                                    if (!window.confirm(
                                        "Create the SUMMERHEAT promo code in Stripe?\n\n" +
                                        "• 10% off\n• No expiry · No minimum order\n• Customers come via fgmapparel.com/?drop=SUMMERHEAT\n\n" +
                                        "Safe to click multiple times — won't duplicate."
                                    )) return;
                                    try {
                                        const res = await axios.post(
                                            `${API}/admin/drop-discounts`,
                                            {
                                                code: "SUMMERHEAT",
                                                percent_off: 10,
                                                duration: "forever",
                                                name: "FGM Drop · Summer Heat",
                                                first_time_transaction: false,
                                            },
                                            { headers: { "X-Admin-Password": password } },
                                        );
                                        const { created, code, percent_off } = res.data;
                                        toast.success(
                                            created
                                                ? `✓ Created ${code} (${percent_off}% off) in Stripe`
                                                : `${code} already exists in Stripe — ready to use`,
                                            { duration: 6000 },
                                        );
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Drop discount create failed");
                                    }
                                }}
                                className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white transition-colors"
                            >
                                + Create SUMMERHEAT Code
                            </button>
                            <button
                                data-testid="create-earlybird-discount"
                                onClick={async () => {
                                    if (!window.confirm(
                                        "Create the EARLYBIRD promo code in Stripe?\n\n" +
                                        "• 15% off (bigger than public SUMMERHEAT)\n• For ambassadors during the 48h pre-launch window\n• Customers come via fgmapparel.com/?drop=EARLYBIRD\n\n" +
                                        "Safe to click multiple times — won't duplicate."
                                    )) return;
                                    try {
                                        const res = await axios.post(
                                            `${API}/admin/drop-discounts`,
                                            {
                                                code: "EARLYBIRD",
                                                percent_off: 15,
                                                duration: "forever",
                                                name: "FGM Drop · Ambassador Early Access",
                                                first_time_transaction: false,
                                            },
                                            { headers: { "X-Admin-Password": password } },
                                        );
                                        const { created, code, percent_off } = res.data;
                                        toast.success(
                                            created
                                                ? `✓ Created ${code} (${percent_off}% off) in Stripe`
                                                : `${code} already exists in Stripe — ready to use`,
                                            { duration: 6000 },
                                        );
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Drop discount create failed");
                                    }
                                }}
                                className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#1D4ED8] hover:text-white transition-colors"
                            >
                                + Create EARLYBIRD Code
                            </button>
                            <button
                                data-testid="backfill-orders"
                                onClick={async () => {
                                    if (!window.confirm(
                                        "Reconcile every PENDING order with Stripe?\n\n" +
                                        "For each pending order, we'll fetch the latest status from Stripe. " +
                                        "If Stripe says it's actually PAID, we'll mark it paid here, fill in " +
                                        "missing customer/shipping details, AND send the receipt + admin notification " +
                                        "emails that the webhook missed.\n\n" +
                                        "Safe to run multiple times — only flips pending → paid (never the reverse)."
                                    )) return;
                                    try {
                                        const res = await axios.post(
                                            `${API}/admin/backfill-orders`,
                                            null,
                                            { headers: { "X-Admin-Password": password } },
                                        );
                                        const { scanned, flipped_to_paid, emails_sent, email_failures, stripe_not_found } = res.data;
                                        toast.success(
                                            `Scanned ${scanned} · flipped ${flipped_to_paid} → paid · ` +
                                            `${emails_sent} receipts sent · ${email_failures} email errors · ` +
                                            `${stripe_not_found} not found on Stripe`,
                                            { duration: 8000 }
                                        );
                                        loadAll();
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Backfill failed");
                                    }
                                }}
                                className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-white text-white hover:bg-white hover:text-black transition-colors"
                            >
                                ↻ Reconcile w/ Stripe
                            </button>
                            <button
                                data-testid="resend-missing-receipts"
                                onClick={async () => {
                                    if (!window.confirm(
                                        "Send the branded FGM order receipt to EVERY paid customer who hasn't received one?\n\nThis is safe to run multiple times. Customers may get a duplicate if they already received one."
                                    )) return;
                                    try {
                                        const res = await axios.post(
                                            `${API}/admin/orders/resend-missing-receipts`,
                                            null,
                                            { headers: { "X-Admin-Password": password } },
                                        );
                                        const { sent, skipped_no_email, failed, total } = res.data;
                                        toast.success(
                                            `Sent ${sent}/${total} · skipped ${skipped_no_email} (no email) · failed ${failed}`
                                        );
                                        loadAll();
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Bulk resend failed");
                                    }
                                }}
                                className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white transition-colors"
                            >
                                ↻ Send Missing Receipts
                            </button>
                        </div>
                        <Table
                            columns={[
                                { key: "session_id", label: "Session", render: (r) => <span className="font-mono text-xs">{(r.session_id || "").slice(-12)}</span> },
                                { key: "amount", label: "Amount", render: (r) => `$${Number(r.amount).toFixed(2)}` },
                                {
                                    key: "payment_status",
                                    label: "Status",
                                    render: (r) => (
                                        <span
                                            className={`font-display tracking-[0.2em] text-xs uppercase ${
                                                r.payment_status === "paid"
                                                    ? "text-[#E63946]"
                                                    : r.payment_status === "pending"
                                                    ? "text-zinc-400"
                                                    : "text-zinc-500"
                                            }`}
                                        >
                                            {r.payment_status}
                                        </span>
                                    ),
                                },
                                {
                                    key: "customer",
                                    label: "Customer",
                                    render: (r) => (
                                        <div className="text-xs leading-tight">
                                            {r.customer_name && <div className="text-white">{r.customer_name}</div>}
                                            <div className="text-zinc-300">{r.customer_email || "—"}</div>
                                            {r.customer_phone && <div className="text-zinc-500">{r.customer_phone}</div>}
                                        </div>
                                    ),
                                },
                                {
                                    key: "shipping_address",
                                    label: "Ship To",
                                    render: (r) => {
                                        const a = r.shipping_address;
                                        if (!a || !a.line1) return <span className="text-zinc-600">—</span>;
                                        return (
                                            <div className="text-xs text-zinc-300 leading-tight max-w-[200px]">
                                                <div>{a.line1}</div>
                                                {a.line2 && <div>{a.line2}</div>}
                                                <div>{[a.city, a.state, a.postal_code].filter(Boolean).join(", ")}</div>
                                                {a.country && <div className="text-zinc-500">{a.country}</div>}
                                            </div>
                                        );
                                    },
                                },
                                {
                                    key: "items_snapshot",
                                    label: "Items",
                                    render: (r) =>
                                        (r.items_snapshot || [])
                                            .map((i) => `${i.quantity}× ${i.name}${i.size ? ` (${i.size})` : ""}`)
                                            .join(", "),
                                },
                                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                                {
                                    key: "actions",
                                    label: "",
                                    render: (r) => (
                                        <button
                                            data-testid={`resend-receipt-${r.session_id}`}
                                            onClick={async () => {
                                                if (!window.confirm(
                                                    `Send order receipt to ${r.customer_email || "this customer"}?`
                                                )) return;
                                                try {
                                                    const res = await axios.post(
                                                        `${API}/admin/orders/${r.session_id}/resend-receipt`,
                                                        null,
                                                        { headers: { "X-Admin-Password": password } },
                                                    );
                                                    toast.success(`Receipt sent to ${res.data.emailed}`);
                                                    loadAll();
                                                } catch (err) {
                                                    toast.error(err?.response?.data?.detail || "Resend failed");
                                                }
                                            }}
                                            className="text-[10px] font-display tracking-[0.25em] uppercase px-2 py-1 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-colors whitespace-nowrap"
                                        >
                                            ↻ Receipt
                                        </button>
                                    ),
                                },
                            ]}
                            rows={orders}
                            empty="No orders yet"
                        />
                    </>
                )}
            </main>
        </div>
    );
}


function FunnelWidget({ password }) {
    const [hours, setHours] = useState(24);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!password) return;
        let alive = true;
        setLoading(true);
        axios
            .get(`${API}/admin/funnel?hours=${hours}`, { headers: { "X-Admin-Password": password } })
            .then((res) => alive && setData(res.data))
            .catch(() => alive && setData(null))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [password, hours]);

    const RANGES = [
        { h: 1, label: "1H" },
        { h: 24, label: "24H" },
        { h: 168, label: "7D" },
        { h: 720, label: "30D" },
    ];
    const maxValue = Math.max(1, ...(data?.stages || []).map((s) => s.value || 0));

    return (
        <div data-testid="funnel-widget" className="bg-fgm-surface border border-white/10 p-6 mb-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                    <div className="font-display text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                        Conversion Funnel
                    </div>
                    <div className="text-white font-display text-lg uppercase tracking-tight">
                        Last {hours < 24 ? `${hours}h` : hours === 24 ? "24 hours" : `${Math.round(hours / 24)} days`}
                    </div>
                </div>
                <div className="flex gap-1">
                    {RANGES.map((r) => (
                        <button
                            key={r.h}
                            data-testid={`funnel-range-${r.h}`}
                            onClick={() => setHours(r.h)}
                            className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border transition-colors ${
                                hours === r.h
                                    ? "bg-[#E63946] border-[#E63946] text-white"
                                    : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading && <div className="text-zinc-500 text-sm">Loading funnel…</div>}
            {!loading && data && (
                <>
                    <div className="space-y-3">
                        {data.stages.map((s, idx) => {
                            const widthPct = data.stages[0].value ? (s.value / data.stages[0].value) * 100 : 0;
                            const fromPrev = s.rate_from_prev;
                            return (
                                <div key={s.label} data-testid={`funnel-stage-${idx}`}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-white text-sm font-medium">{s.label}</span>
                                        <span className="text-white tabular-nums font-display tracking-wider">
                                            {s.value.toLocaleString()}
                                            {fromPrev != null && idx > 0 && (
                                                <span className={`ml-2 text-xs ${fromPrev < 30 ? "text-zinc-500" : "text-[#E63946]"}`}>
                                                    {fromPrev}% ↓
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="h-2 bg-black/60 border border-white/5 overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-500 ${
                                                idx === data.stages.length - 1 ? "bg-[#E63946]" : "bg-white/60"
                                            }`}
                                            style={{ width: `${Math.max(2, widthPct)}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
                        <div>
                            <div className="text-[10px] font-display tracking-[0.3em] text-zinc-500 uppercase mb-1">Revenue</div>
                            <div className="text-[#E63946] font-display text-2xl tabular-nums">${data.revenue.toLocaleString()}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-display tracking-[0.3em] text-zinc-500 uppercase mb-1">Avg. Order</div>
                            <div className="text-white font-display text-2xl tabular-nums">${data.aov}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-display tracking-[0.3em] text-zinc-500 uppercase mb-1">Top-of-Funnel CR</div>
                            <div className="text-white font-display text-2xl tabular-nums">
                                {data.stages[data.stages.length - 1].rate_from_top != null
                                    ? `${data.stages[data.stages.length - 1].rate_from_top}%`
                                    : "—"}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

